import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';

const LeftPanel = ({
    examTitle,
    sectionTitle,
    timeLeft,
    audioUrl,
    isStrictAudio,
    partLabel,
    questionCountLabel,
    instruction,
    storyText,
    imageUrl,
    sharedOptions,
    colorOptions,
    sharedColor,
    setSharedColor,
    wordBank,
    wordBankExampleAnswers,
    answerBank,
    answerBankExampleAnswers,
    optionBank,
    optionBankExampleAnswers,
    inputQuestions,
}) => {
    const { t } = useLanguage();
    const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8002/uploads';
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [hasStarted, setHasStarted] = useState(false);

    const formatTime = (seconds) => {
        if (seconds === null || seconds === undefined || seconds < 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const getSectionEmoji = (title) => {
        if (!title) return '📚';
        const t = title.toUpperCase();
        if (t.includes('LISTEN')) return '🎧';
        if (t.includes('READ')) return '📖';
        if (t.includes('WRIT')) return '✏️';
        if (t.includes('SPEAK')) return '🎤';
        return '⭐';
    };

    const getAudioSrc = (url) => {
        if (!url) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        return `${ASSET_BASE_URL}${url}`;
    };

    const handlePlayStrict = () => {
        if (audioRef.current) {
            if (isStrictAudio) {
                if (!hasStarted) {
                    audioRef.current.play();
                    setIsPlaying(true);
                    setHasStarted(true);
                }
            } else {
                if (isPlaying) {
                    audioRef.current.pause();
                    setIsPlaying(false);
                } else {
                    audioRef.current.play();
                    setIsPlaying(true);
                    setHasStarted(true);
                }
            }
        }
    };

    const handleSeek = (e) => {
        if (!isStrictAudio && audioRef.current) {
            const progressBar = e.currentTarget;
            const rect = progressBar.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const width = rect.width;
            const percentage = Math.min(Math.max(clickX / width, 0), 1);
            const newTime = percentage * audioRef.current.duration;
            audioRef.current.currentTime = newTime;
            setProgress(percentage * 100);
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            const current = audioRef.current.currentTime;
            const total = audioRef.current.duration;
            if (total > 0) {
                setProgress((current / total) * 100);
            }
        }
    };

    const handleAudioEnded = () => {
        setIsPlaying(false);
        setProgress(100);
    };

    useEffect(() => {
        setHasStarted(false);
        setIsPlaying(false);
        setProgress(0);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    }, [audioUrl]);

    const isLowTime = timeLeft !== null && timeLeft !== undefined && timeLeft <= 60;
    const sectionEmoji = getSectionEmoji(sectionTitle);

    // HuPath v2 design tokens
    const HT = {
        yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
        green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
        coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
        brown: '#8B6F47', brownDark: '#6B4F2B',
        text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
        border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
        plum: '#5B2E5E',
    };
    const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
    const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
    const SHADOW_SM = '0 2px 6px rgba(139,111,71,0.08)';
    const SHADOW_MD = '0 4px 16px rgba(139,111,71,0.10)';

    const handleReplay = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play();
            setIsPlaying(true);
            setHasStarted(true);
        }
    };
    const handleSkip = (seconds) => {
        if (audioRef.current && !isStrictAudio) {
            audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime + seconds);
        }
    };
    const audioDuration = audioRef.current?.duration || 0;
    const audioCurrent = audioRef.current?.currentTime || 0;
    const fmt = (s) => {
        if (!s || isNaN(s)) return '0:00';
        const m = Math.floor(s / 60), sec = Math.floor(s % 60);
        return `${m}:${sec < 10 ? '0' : ''}${sec}`;
    };

    return (
        <div className="w-full flex flex-col gap-2 pb-1 min-h-full h-auto" style={{ fontFamily: FONT_BODY, color: HT.text }}>

            {/* 2. Instruction & Content — vertically centered as one group */}
            <div className="flex flex-col justify-center items-center flex-1 h-auto min-h-min gap-3 text-center px-1">

                {/* Part label — greenDark pill */}
                {partLabel && (
                    <div style={{
                        background: HT.greenDark, color: '#fff',
                        boxShadow: SHADOW_SM,
                        padding: '9px 26px', borderRadius: 999,
                        fontFamily: FONT_DISPLAY,
                        fontSize: 17, fontWeight: 800, letterSpacing: 2,
                        textTransform: 'uppercase',
                    }}>{partLabel}</div>
                )}

                {/* Question count — coralDark */}
                {questionCountLabel && (
                    <div style={{ fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 800, color: HT.coralDark, letterSpacing: 1 }}>
                        — {questionCountLabel} —
                    </div>
                )}

                {/* Story / instruction — centered, text color */}
                {storyText && (
                    <p style={{ fontSize: 16, fontWeight: 700, color: HT.text, lineHeight: 1.4, maxWidth: 420, margin: 0 }}>
                        {storyText}
                    </p>
                )}
                {instruction && (
                    <p style={{ fontSize: 15, fontWeight: 700, color: HT.text, opacity: 0.85, lineHeight: 1.4, maxWidth: 420, margin: 0 }}>
                        {instruction}
                    </p>
                )}

                {/* Audio Player — v2 pill */}
                {audioUrl && (
                    <div className="w-full flex flex-col items-center gap-3" style={{ marginTop: 10 }}>
                        <audio
                            ref={audioRef}
                            src={getAudioSrc(audioUrl)}
                            preload="auto"
                            onTimeUpdate={handleTimeUpdate}
                            onEnded={handleAudioEnded}
                        />
                        <div
                            style={{
                                width: '100%', maxWidth: 440,
                                background: '#fff',
                                border: `1px solid ${HT.border}`, boxShadow: SHADOW_MD,
                                borderRadius: 999,
                                padding: '8px 16px 8px 8px',
                                display: 'flex', alignItems: 'center', gap: 12,
                            }}
                        >
                            <button
                                onClick={handlePlayStrict}
                                disabled={isStrictAudio && hasStarted}
                                style={{
                                    width: 52, height: 52, borderRadius: 999,
                                    background: isStrictAudio && hasStarted ? '#E5E0D5' : HT.teal,
                                    border: 'none',
                                    boxShadow: isStrictAudio && hasStarted ? 'none' : SHADOW_SM,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: HT.white, cursor: isStrictAudio && hasStarted ? 'not-allowed' : 'pointer',
                                    flexShrink: 0, fontSize: 18, fontWeight: 800,
                                    padding: 0,
                                }}
                                aria-label="Play"
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: 26, fontVariationSettings: "'FILL' 1" }}>
                                    {isStrictAudio ? (hasStarted ? 'volume_up' : 'play_arrow') : (isPlaying ? 'pause' : 'play_arrow')}
                                </span>
                            </button>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 10, fontWeight: 800, color: HT.greenDark, letterSpacing: 2 }}>
                                    {(isPlaying ? t('playing') : t('audioTrack')).toUpperCase()}
                                </div>
                                <div
                                    onClick={handleSeek}
                                    style={{
                                        marginTop: 5, height: 8,
                                        background: HT.greenBg, borderRadius: 999,
                                        padding: 1, overflow: 'hidden',
                                        cursor: !isStrictAudio ? 'pointer' : 'default',
                                    }}
                                >
                                    <div style={{
                                        width: `${progress}%`, height: '100%',
                                        background: HT.coral, borderRadius: 999,
                                        transition: 'width 0.15s linear',
                                    }} />
                                </div>
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 800, color: HT.textMuted, whiteSpace: 'nowrap' }}>
                                {fmt(audioCurrent)}/{fmt(audioDuration)}
                            </div>
                        </div>


                        {/* Hint tip */}
                        {isStrictAudio && (
                            <div style={{
                                padding: '7px 14px',
                                background: '#fff', border: `1px dashed ${HT.border}`, borderRadius: 10,
                                fontSize: 11, fontWeight: 700, color: HT.textSoft,
                            }}>Bạn chỉ được nghe 1 lần khi thi</div>
                        )}
                    </div>
                )}
                    {(imageUrl || (sharedOptions && sharedOptions.length > 0) || (colorOptions && colorOptions.length > 0) || (wordBank && wordBank.length > 0) || (answerBank && Object.keys(answerBank).length > 0) || (optionBank && optionBank.length > 0)) && (
                        <div className="mt-2 flex flex-col items-center gap-2">
                            {imageUrl && (
                                <div className="w-full shrink-0 flex justify-center mb-4">
                                    <img
                                        src={`${ASSET_BASE_URL}${imageUrl}`}
                                        alt="Exercise illustration"
                                        className="rounded-xl w-auto max-w-full max-h-52 object-contain shadow-sm border border-gray-100 p-1 bg-white"
                                    />
                                </div>
                            )}
                            {sharedOptions && sharedOptions.length > 0 && (
                                <div className={`flex-1 min-h-0 grid gap-1.5 w-full content-center ${sharedOptions.length > 9 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                                    {sharedOptions.map(option => {
                                        return (
                                            <div key={option.id} className="flex flex-col items-center gap-1 p-1.5 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-amber-200 dark:border-slate-700 min-h-0">
                                                <img src={`${ASSET_BASE_URL}${option.image_url}`} alt={option.image_text || `Option ${option.id}`} className="min-h-0 flex-1 w-full object-contain rounded-md" />
                                                <span className="shrink-0 font-display font-bold text-xs text-slate-700 dark:text-slate-300 text-center leading-tight">{option.image_text || option.id}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {/* Word Bank — inside content card like image */}
                            {wordBank && wordBank.length > 0 && (
                                <div className="mt-1 w-full p-2 rounded-3xl border-2 border-hupath-scout/20 bg-gradient-to-br from-hupath-scout/5 to-white shadow-sm overflow-hidden relative">
                                    <div className="absolute -top-6 -right-6 w-16 h-16 bg-hupath-scout/10 rounded-full blur-2xl" />
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-xl bg-hupath-scout flex items-center justify-center shadow-sm">
                                            <span className="material-symbols-outlined text-white text-base">format_list_bulleted</span>
                                        </div>
                                        <p className="font-headline font-black text-hupath-scout text-xs uppercase tracking-widest">{t('wordBank')}</p>
                                    </div>
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                                        {wordBank.map(word => {
                                            const isExample = wordBankExampleAnswers && wordBankExampleAnswers.includes(word);
                                            return (
                                                <div
                                                    key={word}
                                                    className={`px-3 py-2.5 rounded-2xl text-center flex items-center justify-center text-sm font-headline font-black border-2 transition-all relative ${isExample
                                                        ? 'bg-hp-surface-container/50 text-hp-on-surface/30 border-hp-surface-container line-through'
                                                        : 'bg-white text-hp-on-surface border-hupath-scout/10 hover:border-hupath-scout hover:shadow-md hover:-translate-y-0.5 shadow-sm'
                                                        }`}
                                                >
                                                    {word.replace(/\(Example\)/gi, '').trim()}
                                                    {isExample && (
                                                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-hp-surface-container text-[10px] rounded-full flex items-center justify-center shadow-sm">✓</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {colorOptions && colorOptions.length > 0 && (
                                <div className="mt-4 p-4 bg-white dark:bg-slate-800 rounded-2xl border-2 border-amber-200 dark:border-slate-600 shadow-sm">
                                    <p className="font-bold text-amber-700 dark:text-amber-300 text-sm mb-3 uppercase tracking-wider text-center">{t('colorPalette')}</p>
                                    <div className="flex justify-center flex-wrap gap-3">
                                        {colorOptions.map(color => (
                                            <div
                                                key={color}
                                                className={`w-10 h-10 rounded-full cursor-pointer border-4 border-white dark:border-gray-800 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md ${sharedColor === color ? 'ring-4 ring-primary-blue scale-110 shadow-lg' : 'hover:scale-105'}`}
                                                style={{ backgroundColor: color.toLowerCase() }}
                                                onClick={() => setSharedColor && setSharedColor(color)}
                                                title={color}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {inputQuestions && inputQuestions.length > 0 && (
                                <div className="mt-4 p-4 bg-white dark:bg-slate-800 rounded-2xl border-2 border-purple-200 dark:border-slate-600 shadow-sm">
                                    <p className="font-bold text-purple-700 dark:text-purple-300 text-sm mb-1 uppercase tracking-wider text-center">{t('fillWord')}</p>
                                    <p className="text-xs text-purple-400 text-center mb-3">{t('dragToImage').replace('{count}', inputQuestions.length)}</p>
                                    <div
                                        draggable
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData('type', 'inputBox');
                                            // Ghost image matches the rendered input box size (160x44)
                                            const ghost = document.createElement('div');
                                            ghost.style.cssText = 'position:fixed;top:-200px;left:0;width:160px;height:44px;background:rgba(243,232,255,0.95);border:2px dashed #a855f7;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:bold;color:#7e22ce;';
                                            ghost.textContent = t('typeWord');
                                            document.body.appendChild(ghost);
                                            e.dataTransfer.setDragImage(ghost, 80, 22);
                                            setTimeout(() => document.body.removeChild(ghost), 0);
                                        }}
                                        className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-50 dark:bg-purple-900/30 border-2 border-dashed border-purple-400 dark:border-purple-500 rounded-xl cursor-grab active:cursor-grabbing hover:shadow-md hover:-translate-y-0.5 hover:bg-purple-100 transition-all select-none"
                                    >
                                        <span className="material-symbols-outlined text-purple-500 text-lg">drag_indicator</span>
                                        <span className="text-sm font-bold text-purple-700 dark:text-purple-300">{t('dragBox')}</span>
                                        <span className="material-symbols-outlined text-purple-400 text-base">text_fields</span>
                                    </div>
                                </div>
                            )}
                            {/* Answer Choices — for Reading Part 2 */}
                            {answerBank && Object.keys(answerBank).length > 0 && (
                                <div className="mt-1 w-full p-2 rounded-3xl border-2 border-hupath-scout/20 bg-gradient-to-br from-hupath-scout/5 to-white shadow-sm overflow-hidden relative">
                                    <div className="absolute -top-6 -right-6 w-16 h-16 bg-hupath-scout/10 rounded-full blur-2xl" />
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-xl bg-hupath-scout flex items-center justify-center shadow-sm">
                                            <span className="material-symbols-outlined text-white text-base">grid_view</span>
                                        </div>
                                        <p className="font-headline font-black text-hupath-scout text-xs uppercase tracking-widest">{t('answerChoices')}</p>
                                    </div>
                                    <div className="space-y-2">
                                        {Object.entries(answerBank).map(([letter, text]) => {
                                            const isExample = answerBankExampleAnswers && answerBankExampleAnswers.includes(letter);
                                            return (
                                                <div key={letter} className={`flex items-start gap-3 p-3 rounded-2xl transition-all ${isExample ? 'bg-hp-surface-container/40 opacity-70' : 'bg-white shadow-sm border border-hupath-scout/5 hover:border-hupath-scout/20'}`}>
                                                    <span className={`shrink-0 font-headline font-black w-7 h-7 rounded-full flex items-center justify-center text-xs border-2 shadow-sm ${isExample ? 'bg-hp-surface-container text-hp-on-surface/40 border-hp-surface-container' : 'bg-hupath-scout/10 text-hupath-scout border-hupath-scout/20'}`}>{letter}</span>
                                                    <div className="flex-1">
                                                        <p className={`text-sm font-medium m-0 leading-relaxed ${isExample ? 'text-hp-on-surface/40 line-through' : 'text-hp-on-surface'}`}>
                                                            {text.replace(/\(Example\)/gi, '').trim()}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {/* Option Bank — for Reading Part 4, styled like Word Bank */}
                            {optionBank && optionBank.length > 0 && (
                                <div className="mt-1 w-full p-2 rounded-3xl border-2 border-hupath-scout/20 bg-gradient-to-br from-hupath-scout/5 to-white shadow-sm overflow-hidden relative">
                                    <div className="absolute -top-6 -right-6 w-16 h-16 bg-hupath-scout/10 rounded-full blur-2xl" />
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-xl bg-hupath-scout flex items-center justify-center shadow-sm">
                                            <span className="material-symbols-outlined text-white text-base">format_list_bulleted</span>
                                        </div>
                                        <p className="font-headline font-black text-hupath-scout text-xs uppercase tracking-widest">{t('chooseCorrectWords')}</p>
                                    </div>
                                    <div className="space-y-2">
                                        {optionBank.map(item => (
                                            <div key={item.no} className="flex items-center gap-2">
                                                <div className={`shrink-0 flex items-center justify-center bg-hupath-scout/10 text-hupath-scout font-black text-xs border border-hupath-scout/20 shadow-sm ${item.no === 0 ? 'px-2 py-1 rounded-lg' : 'w-7 h-7 rounded-full'}`}>
                                                    {item.no === 0 ? 'Eg' : item.no}
                                                </div>
                                                <div className="flex-1 grid grid-cols-3 gap-2">
                                                    {item.options.map((option, index) => {
                                                        const isExampleAnswer = item.no === 0 && optionBankExampleAnswers && optionBankExampleAnswers.includes(option);
                                                        return (
                                                            <div
                                                                key={index}
                                                                className={`px-4 py-2.5 rounded-2xl text-center flex items-center justify-center text-sm font-headline font-black border-2 transition-all relative ${isExampleAnswer
                                                                    ? 'bg-hp-surface-container/50 text-hp-on-surface/30 border-hp-surface-container line-through'
                                                                    : 'bg-white text-hp-on-surface border-hupath-scout/10 hover:border-hupath-scout hover:shadow-md hover:-translate-y-0.5 shadow-sm'
                                                                    }`}
                                                            >
                                                                {option.replace(/\(Example\)/gi, '').trim()}
                                                                {isExampleAnswer && (
                                                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-hp-surface-container text-[10px] rounded-full flex items-center justify-center shadow-sm">✓</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
        </div>
    );
};

export default LeftPanel;
